/**
 * FriendSelectorList
 * Reusable list of friends with multi-selection and optional search.
 * Data from FriendsDataContext; filter by accepted and excludeIds.
 * Grid layout reuses FriendsGrid + FriendCardAccepted (same as CreateParticipantsStep).
 */

import React, { useState, useMemo, useCallback } from 'react'
import { View, Text, TextInput, Pressable, ScrollView, Dimensions, Platform } from 'react-native'
import type { RefObject } from 'react'
import { SoundPressable } from '../../../src/shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import { Image } from 'expo-image'
import { useFriendsData } from '../../../contexts/FriendsDataContext'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { FriendAvatar } from '../shared/FriendAvatar'
import { FriendsGrid } from '../FriendsList/FriendsGrid'
import { FriendCardAccepted } from '../FriendCard/FriendCardAccepted'
import { InviteCard } from '../InviteCard/InviteCard'
import { AddGroupCard } from '../AddGroupCard/AddGroupCard'
import { getFriendCardStyles } from '../FriendCard/FriendCard.styles'
import { getSelectableFriends } from '../../../src/friends/helpers/getSelectableFriends.helper'
import { getSafeDisplayName } from '../../../src/create/helpers/createUi.helper'
import { IDLE_COIN_SOURCE } from '../../../constants/assets'
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper'
import { getFriendSelectorListStyles } from './FriendSelectorList.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import { calculateColumns } from '../../../src/shared/helpers/calculateColumns.helper'
import type { FriendsGridItem } from '../../../src/friends/helpers/unifiedList.helper'


/** Min card width when grid uses contentWidth (modal); ensures cards stay usable on S/M phones. */
const MIN_INVITE_CARD_WIDTH = 100
/** On narrow screens (phone), use smaller min so we get more columns and smaller cards. */
const MIN_INVITE_CARD_WIDTH_PHONE = 80
const INVITE_GRID_GAP = 16
const INVITE_GRID_H_PADDING = 32
const NARROW_BREAKPOINT = 400

export type FriendSelectorListLayout = 'row' | 'grid'

export interface FriendSelectorListProps {
  selectedIds: Set<string>
  onToggle: (id: string) => void
  excludeIds?: string[]
  /** When false, parent should pass onToggle that replaces selection (e.g. setSelectedIds(new Set([id]))). */
  multiSelect?: boolean
  /** 'row' = list rows, 'grid' = square cards */
  layout?: FriendSelectorListLayout
  /** When layout is grid, override column count for smaller cards (e.g. 4 or 5). */
  gridNumColumns?: number
  /** When true (e.g. in invite selector), hide win rate on cards. Default false. */
  hideWinRate?: boolean
  /** When true (e.g. invite modal), use smaller text so it fits in narrow cards. Default false. */
  compactText?: boolean
  /** When set (e.g. modal body width), grid uses this instead of window width so cards fit the container. */
  contentWidth?: number
  /** When true, hide the search bar (e.g. create challenge participant step). Default false. */
  hideSearch?: boolean
  /** When set, append this card as last item in the grid (e.g. Invite or Create group). */
  appendCard?: { type: 'INVITE' | 'CREATE_GROUP'; id: string }
  /** Called when the appended card is pressed. */
  onAppendCardPress?: () => void
  /** Disable selection for these IDs while still rendering the card. */
  disabledIds?: Set<string>
  /** When true, show each friend's coin balance instead of win/loss stats. */
  showCoins?: boolean
  /** When set, the card for this friend ID will be wrapped with a ref for walkthrough spotlighting. */
  walkthroughTargetFriendId?: string | null
  /** Ref to attach to the walkthrough target card's wrapper View. */
  walkthroughCardRef?: RefObject<View | null>
  /**
   * Grid layout only: render a custom card for each friend instead of the default FriendCardAccepted.
   * The custom card is presentational (this list still owns press / disabled / walkthrough wiring).
   * Used by the create flow to show the dark "call someone out" mascot card.
   */
  renderFriendCard?: (args: {
    friend: import('../../../src/friends/types/friends.types').Friend
    cardWidth: number
    isSelected: boolean
    isDisabled: boolean
  }) => React.ReactNode
  /**
   * Grid layout only: render the appended card yourself instead of the default Invite / Create tile.
   * Needed when `renderFriendCard` supplies cards the default tile can't size itself against
   * (the tile only learns `cardWidth` here, inside the grid). Press wiring stays with this list.
   */
  renderAppendCard?: (args: { cardWidth: number }) => React.ReactNode
  /** Test ID for testing */
  testID?: string
}

export const FriendSelectorList: React.FC<FriendSelectorListProps> = ({
  selectedIds,
  onToggle,
  excludeIds = [],
  multiSelect: _multiSelect = true,
  layout = 'row',
  gridNumColumns,
  hideWinRate = false,
  compactText = false,
  contentWidth,
  hideSearch = false,
  appendCard,
  onAppendCardPress,
  disabledIds,
  showCoins = false,
  walkthroughTargetFriendId = null,
  walkthroughCardRef,
  renderFriendCard,
  renderAppendCard,
  testID,
}) => {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const styles = useMemo(() => getFriendSelectorListStyles(theme), [theme])
  const { friends, loading } = useFriendsData()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredFriends = useMemo(
    () =>
      getSelectableFriends(friends, {
        excludeIds: excludeIds ?? [],
        searchQuery,
      }),
    [friends, excludeIds, searchQuery]
  )

  const handleToggle = useCallback(
    (id: string) => {
      onToggle(id)
    },
    [onToggle]
  )

  const gridItems = useMemo((): FriendsGridItem[] => {
    const friendItems: FriendsGridItem[] = filteredFriends.map((friend) => ({
      type: 'FRIEND',
      id: friend.id,
      friend,
    }))
    if (appendCard && layout === 'grid') {
      return [...friendItems, appendCard as FriendsGridItem]
    }
    return friendItems
  }, [filteredFriends, appendCard, layout])
  const screenWidth =
    layout === 'grid' && contentWidth != null && contentWidth > 0
      ? contentWidth
      : Dimensions.get('window').width
  const numColumns = useMemo(() => {
    if (layout !== 'grid') return calculateColumns(screenWidth)
    // When parent passes both (e.g. create step), use that count to match friends tab exactly
    if (contentWidth != null && contentWidth > 0 && gridNumColumns != null) {
      return gridNumColumns
    }
    const isNarrow =
      (contentWidth != null && contentWidth < NARROW_BREAKPOINT) ||
      Dimensions.get('window').width < NARROW_BREAKPOINT
    const maxCols = isNarrow ? 5 : (gridNumColumns ?? 4)
    if (contentWidth == null || contentWidth <= 0) {
      return gridNumColumns != null ? gridNumColumns : calculateColumns(screenWidth)
    }
    const minCard = isNarrow ? MIN_INVITE_CARD_WIDTH_PHONE : MIN_INVITE_CARD_WIDTH
    const available = contentWidth - INVITE_GRID_H_PADDING
    const colsByMinWidth = Math.floor(available / (minCard + INVITE_GRID_GAP))
    const cols = Math.min(maxCols, Math.max(2, colsByMinWidth))
    return cols
  }, [layout, gridNumColumns, screenWidth, contentWidth])
  const friendCardStyles = useMemo(() => getFriendCardStyles(theme, themeMode), [theme, themeMode])
  const gridCardAndroidRipple = useMemo(
    () =>
      Platform.OS === 'android'
        ? { color: `${theme.primary}33`, borderless: false, foreground: true as const }
        : undefined,
    [theme.primary]
  )
  const renderGridCard = useCallback(
    ({ item, cardWidth }: { item: FriendsGridItem; index: number; cardWidth: number }) => {
      if ((item.type === 'INVITE' || item.type === 'CREATE_GROUP') && renderAppendCard) {
        return <>{renderAppendCard({ cardWidth })}</>
      }
      if (item.type === 'INVITE' && onAppendCardPress) {
        return (
          <InviteCard
            cardWidth={cardWidth}
            onPress={onAppendCardPress}
            testID={testID ? `${testID}-invite-card` : 'invite-card'}
          />
        )
      }
      if (item.type === 'CREATE_GROUP' && onAppendCardPress) {
        return (
          <AddGroupCard
            cardWidth={cardWidth}
            onPress={onAppendCardPress}
            testID={testID ? `${testID}-add-group-card` : 'add-group-card'}
          />
        )
      }
      if (!('friend' in item)) return null
      const friend = item.friend
      const isSelected = selectedIds.has(friend.id)
      const isDisabled = disabledIds?.has(friend.id) ?? false
      const rowTestID = testID ? `${testID}-row-${friend.id}` : undefined
      const isWalkthroughTarget = walkthroughTargetFriendId != null && friend.id === walkthroughTargetFriendId

      // Custom card override (e.g. create-flow mascot card). The override is presentational;
      // this Pressable still owns press / disabled / ripple, and the walkthrough ref wrapping.
      if (renderFriendCard) {
        const customCard = (
          <Pressable
            // The custom card owns its own selected/disabled visuals; here we only add press feedback.
            style={({ pressed }) => (!isDisabled && pressed ? styles.gridCardPressed : null)}
            android_ripple={isDisabled ? undefined : gridCardAndroidRipple}
            onPress={() => handleToggle(friend.id)}
            disabled={isDisabled}
            testID={rowTestID}
          >
            {renderFriendCard({ friend, cardWidth, isSelected, isDisabled })}
          </Pressable>
        )
        if (isWalkthroughTarget) {
          return (
            <View ref={walkthroughCardRef} collapsable={false}>
              {customCard}
            </View>
          )
        }
        return customCard
      }

      const card = (
        <Pressable
          style={({ pressed, hovered }) => [
            friendCardStyles.friendCardSelectionHost,
            isSelected ? friendCardStyles.friendCardSelected : friendCardStyles.friendCardUnselected,
            isDisabled && styles.gridCardDisabled,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            !isDisabled && pressed && styles.gridCardPressed,
            !isDisabled && hovered && Platform.OS === 'web' && styles.gridCardHover,
          ]}
          android_ripple={isDisabled ? undefined : gridCardAndroidRipple}
          onPress={() => handleToggle(friend.id)}
          disabled={isDisabled}
          testID={rowTestID}
        >
          <FriendCardAccepted
            friend={friend}
            cardWidth={cardWidth}
            onSelect={() => {
              if (isDisabled) return
              handleToggle(friend.id)
            }}
            isSelected={isSelected}
            disabled={isDisabled}
            showWinRate={!hideWinRate}
            showCoins={showCoins}
            compactText={compactText}
          />
        </Pressable>
      )
      if (isWalkthroughTarget) {
        return (
          <View ref={walkthroughCardRef} collapsable={false}>
            {card}
          </View>
        )
      }
      return card
    },
    [
      selectedIds,
      disabledIds,
      handleToggle,
      hideWinRate,
      showCoins,
      compactText,
      testID,
      friendCardStyles,
      gridCardAndroidRipple,
      onAppendCardPress,
      walkthroughTargetFriendId,
      walkthroughCardRef,
      renderFriendCard,
      renderAppendCard,
      styles,
    ]
  )

  if (loading) {
    return (
      <View style={styles.container} testID={testID}>
        <View style={styles.loading}>
          <Text style={styles.emptyText}>Loading friends...</Text>
        </View>
      </View>
    )
  }

  const emptyState = (
    <View style={styles.empty}>
      <Text style={styles.emptyText}>
        {friends.length === 0 ? 'No friends yet' : 'No matching friends'}
      </Text>
    </View>
  )

  const gridContent = (
    <View style={styles.gridWrapper}>
      <FriendsGrid
        items={gridItems}
        renderCard={renderGridCard}
        numColumns={numColumns}
        screenWidth={screenWidth}
        contentWidth={contentWidth}
        extraData={Array.from(selectedIds).sort().join(',')}
        testID={testID ? `${testID}-grid` : undefined}
      />
    </View>
  )

  const rowContent =
    layout === 'row' ? (
      <ScrollView style={styles.list} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
        {filteredFriends.length === 0
          ? emptyState
          : filteredFriends.map((friend) => {
              const isSelected = selectedIds.has(friend.id)
              const isDisabled = disabledIds?.has(friend.id) ?? false
              return (
                <SoundPressable
                  key={friend.id}
                  tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
                  style={({ pressed, hovered }) => [
                    styles.row,
                    isSelected && styles.rowSelected,
                    isDisabled && styles.rowDisabled,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    !isDisabled && pressed && styles.rowPressed,
                    !isDisabled && hovered && Platform.OS === 'web' && styles.rowHover,
                  ]}
                  onPress={() => handleToggle(friend.id)}
                  disabled={isDisabled}
                  testID={testID ? `${testID}-row-${friend.id}` : undefined}
                >
                  <View style={styles.avatarWrap}>
                    <FriendAvatar
                      size={36}
                      avatarId={friend.avatarId ?? undefined}
                      avatarConfig={friend.avatarConfig}
                      fallbackUrl={friend.avatar?.startsWith('http') ? friend.avatar : undefined}
                      small={false}
                      nameForInitials={friend.name}
                    />
                  </View>
                  <View style={styles.nameBlock}>
                    <Text style={styles.name} numberOfLines={1}>
                      {getSafeDisplayName(friend.name, friend.email)}
                    </Text>
                    {showCoins && (
                      <View style={styles.rowCoinsRow}>
                        <Text style={styles.subtitle} numberOfLines={1}>
                          {(friend.coins ?? 0).toLocaleString()}
                        </Text>
                        <Image
                          source={IDLE_COIN_SOURCE}
                          style={styles.rowCoinIcon}
                          alt={WEB_IMAGE_ALT_DECORATIVE}
                        />
                      </View>
                    )}
                  </View>
                  <View style={[styles.checkWrap, isSelected && styles.checkWrapSelected]}>
                    {isSelected && <Text style={styles.checkText}>✓</Text>}
                  </View>
                </SoundPressable>
              )
            })}
      </ScrollView>
    ) : null

  return (
    <View style={styles.container} testID={testID}>
      {!hideSearch && (
        <TextInput
          style={styles.searchInput}
          placeholder="Search by name or email"
          placeholderTextColor={theme.gray[500]}
          value={searchQuery}
          onChangeText={setSearchQuery}
          testID={testID ? `${testID}-search` : 'friend-selector-search'}
        />
      )}
      {filteredFriends.length === 0 ? emptyState : layout === 'grid' ? gridContent : rowContent}
    </View>
  )
}
